/**
 * 줄 단위 비교 — PL/SQL 편집기의 「덮어쓰기 충돌」을 보여 주는 데 쓴다.
 *
 * 앞뒤 공통 줄을 먼저 떼고(대부분의 편집은 한 군데다) **가운데만** LCS 로 맞춘다.
 * 가운데가 너무 크면(표가 메모리를 먹는다) 가운데를 통째로 「지움 + 추가」로 보인다 —
 * 정확한 짝은 못 맞추지만 틀린 말은 하지 않는다.
 *
 * @returns {Array<{ type: 'same'|'del'|'add', text: string }>}
 *          del = 앞(a)에만 있음, add = 뒤(b)에만 있음
 */
const MAX_CELLS = 4_000_000;

export function splitLines(text) {
    return String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n');
}

export function lineDiff(aText, bText) {
    const a = splitLines(aText);
    const b = splitLines(bText);

    let pre = 0;
    while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
    let suf = 0;
    while (suf < a.length - pre && suf < b.length - pre
        && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++;

    const out = [];
    for (let i = 0; i < pre; i++) out.push({ type: 'same', text: a[i] });

    const am = a.slice(pre, a.length - suf);
    const bm = b.slice(pre, b.length - suf);
    const n = am.length;
    const m = bm.length;

    if (n * m > MAX_CELLS) {
        am.forEach(text => out.push({ type: 'del', text }));
        bm.forEach(text => out.push({ type: 'add', text }));
    } else if (n > 0 || m > 0) {
        // dp[i][j] = am[i..] 와 bm[j..] 의 LCS 길이 (1차원 배열로)
        const w = m + 1;
        const dp = new Uint32Array((n + 1) * w);
        for (let i = n - 1; i >= 0; i--) {
            for (let j = m - 1; j >= 0; j--) {
                dp[i * w + j] = am[i] === bm[j]
                    ? dp[(i + 1) * w + (j + 1)] + 1
                    : Math.max(dp[(i + 1) * w + j], dp[i * w + (j + 1)]);
            }
        }
        let i = 0;
        let j = 0;
        while (i < n && j < m) {
            if (am[i] === bm[j]) { out.push({ type: 'same', text: am[i] }); i++; j++; }
            else if (dp[(i + 1) * w + j] >= dp[i * w + (j + 1)]) { out.push({ type: 'del', text: am[i] }); i++; }
            else { out.push({ type: 'add', text: bm[j] }); j++; }
        }
        while (i < n) out.push({ type: 'del', text: am[i++] });
        while (j < m) out.push({ type: 'add', text: bm[j++] });
    }

    for (let k = b.length - suf; k < b.length; k++) out.push({ type: 'same', text: b[k] });
    return out;
}

/**
 * 바뀐 곳 둘레만 남긴다 — 수천 줄 프로시저에서 한 줄 바뀐 것을 찾으러 스크롤하지 않게.
 * 접힌 자리는 `{ type: 'gap', count }` 로 남긴다(몇 줄을 숨겼는지 말한다).
 */
export function withContext(diff, context = 3) {
    const keep = new Array(diff.length).fill(false);
    diff.forEach((d, idx) => {
        if (d.type === 'same') return;
        for (let k = Math.max(0, idx - context); k <= Math.min(diff.length - 1, idx + context); k++) keep[k] = true;
    });
    const out = [];
    let hidden = 0;
    diff.forEach((d, idx) => {
        if (keep[idx]) {
            if (hidden) { out.push({ type: 'gap', count: hidden }); hidden = 0; }
            out.push(d);
        } else {
            hidden++;
        }
    });
    if (hidden) out.push({ type: 'gap', count: hidden });
    return out;
}

/** 비교 전에 맞춘다 — 줄바꿈 종류·줄 끝 공백·앞뒤 빈 줄은 「바뀜」이 아니다 */
export function normalizeSource(text) {
    return splitLines(text).map(l => l.replace(/\s+$/, '')).join('\n').trim();
}
